/**
 * Local synced-template store — the templates the user has SYNCED onto THIS device.
 *
 * The cloud catalog (`client.listTemplates`) is the browse surface on the Templates page.
 * Syncing a template persists its FULL record (with geometry) here, in the per-device
 * `localStore.records` (IndexedDB on both web and desktop — the same store the queue,
 * logger and telemetry use). The Mockup Generator then reads ONLY from here, so creating a
 * mockup never touches the network and selecting a template is instant (geometry is local).
 *
 * ENCRYPTED AT REST: the template JSON (its geometry) is AES-GCM encrypted before it touches
 * the store — the record value is a base64 blob, only the template id is stored in the clear
 * (it's the row key). Re-syncing overwrites the local copy (`put` upserts by id). Rows written
 * before this (plain records) still read back (the `enc` field gates decryption).
 */
import type { CaseStyleOption, PhoneModelOption, PlatformIO, ProductTemplate } from "@genz/platform";
import { getBlobKey, encryptString, decryptString } from "../stores/blobCrypto.js";
import { deleteMockup } from "../stores/mockupStore.js";
import { deleteTemplateImage } from "../stores/imageStore.js";
import { PRIMARY_VIEW } from "./views.js";
import { HIDDEN_LOCAL_KEY } from "./deviceHidden.js";

/** Just the storage slice these helpers need — keeps them trivially unit-testable. */
type StoreIO = Pick<PlatformIO, "localStore">;

const SYNCED_TEMPLATES = "synced_templates";
const PHONE_MODELS_KEY = "synced_phone_models";
const CASE_STYLES_KEY = "synced_case_styles";

/**
 * The device-local storage this module OWNS, named once so `app/signOut` purges exactly what
 * is written here.
 *
 * `signOut` used to carry its own copy of the collection name and nothing else, so the two
 * catalog caches below were invisible to it and outlived the account they were fetched for.
 * Both are platform-global, so nothing leaked — but the routine's contract is "everything
 * account-scoped is dropped", and a second copy of a key string is how that contract goes
 * quietly out of date.
 */
export const LOCAL_TEMPLATE_STORAGE = {
  /** Records collection: the encrypted synced-template rows. */
  records: SYNCED_TEMPLATES,
  /** KV keys: the cached catalog lists the OFFLINE funnel resolves names from. */
  kv: [PHONE_MODELS_KEY, CASE_STYLES_KEY] as const,
  /**
   * localStorage keys this module's surfaces own. The per-device "removed from this device"
   * list is account-scoped state (`deviceHidden.ts`) that `signOut` never dropped: the next
   * account inherited the previous one's hidden template ids, and on the Mock-ups page that
   * silently withheld rows it had no business withholding.
   */
  local: [HIDDEN_LOCAL_KEY] as const,
} as const;

/**
 * The at-rest row: the template id in the clear (row key), the encrypted JSON payload, and
 * WHEN this device wrote it.
 *
 * `savedAt` exists for the reconcile's grace window. The Mock-ups page evicts local rows the
 * cloud listing does not return — and a template synced SECONDS ago is not returned by a
 * listing that was fetched before it existed, so without a "written just now" stamp a Sync on
 * the Templates page racing a reconcile on the Mock-ups page could delete what the Sync had
 * just downloaded. Optional: rows written before this read back with `undefined`, which is
 * correct (they are not recent).
 */
interface EncRow {
  id: string;
  enc: string;
  savedAt?: number;
}
/** The same envelope when WebCrypto is unavailable — the payload is the bare record. */
interface PlainRow {
  id: string;
  plain: ProductTemplate;
  savedAt?: number;
}
function isEncRow(r: unknown): r is EncRow {
  return !!r && typeof r === "object" && typeof (r as EncRow).enc === "string";
}
function isPlainRow(r: unknown): r is PlainRow {
  const p = (r as PlainRow | null)?.plain;
  return !!r && typeof r === "object" && !!p && typeof p === "object" && typeof p.templateId === "string";
}

/** One stored row, with the metadata the eviction rules need. */
export interface SyncedTemplateRow {
  template: ProductTemplate;
  /** Epoch-ms of this device's last write; `undefined` for rows written before the stamp. */
  savedAt?: number;
}

/** Unwrap one stored record into `{ template, savedAt }`, or null when it cannot be read. */
async function unwrapRow(r: unknown): Promise<SyncedTemplateRow | null> {
  if (isEncRow(r)) {
    const key = await getBlobKey();
    if (!key) return null; // encrypted but no key → can't read (skip rather than crash)
    try {
      return { template: JSON.parse(await decryptString(key, r.enc)) as ProductTemplate, savedAt: r.savedAt };
    } catch {
      return null; // corrupt/undecryptable row
    }
  }
  if (isPlainRow(r)) return { template: r.plain, savedAt: r.savedAt };
  if (r && typeof r === "object" && typeof (r as ProductTemplate).templateId === "string") {
    return { template: r as ProductTemplate }; // legacy bare row — no stamp, and that is correct
  }
  return null;
}

/** Persist (or overwrite) a synced template's full record on this device — encrypted. */
export async function saveSyncedTemplate(io: StoreIO, t: ProductTemplate): Promise<void> {
  const key = await getBlobKey();
  const savedAt = Date.now();
  const record: EncRow | PlainRow = key
    ? { id: t.templateId, enc: await encryptString(key, JSON.stringify(t)), savedAt }
    : { id: t.templateId, plain: t, savedAt }; // WebCrypto unavailable → plaintext (best-effort)
  await io.localStore.records.put(SYNCED_TEMPLATES, t.templateId, record);
}

/** One synced template's full record, decrypted — or null when absent/unreadable. */
async function readSyncedTemplate(io: StoreIO, templateId: string): Promise<ProductTemplate | null> {
  try {
    const r = await io.localStore.records.get<unknown>(SYNCED_TEMPLATES, templateId);
    return (await unwrapRow(r))?.template ?? null;
  } catch {
    return null; // corrupt/undecryptable → nothing to derive the asset keys from
  }
}

/**
 * Remove a template from this device (Unsync) — the record AND the assets the sync downloaded
 * alongside it.
 *
 * Removing only the record left the baked mockups (multi-MB per view, in `genz-mockups`) behind
 * forever: nothing prunes that store, and its keys are VERSION-scoped, so once the template
 * record is gone no later sweep can even work out which rows belonged to it. The version id and
 * the view list live only in the record, so it is read BEFORE it is dropped.
 *
 * Best-effort on the asset side — `deleteMockup` never throws, and a template that can't be
 * decrypted still un-syncs (it just can't name its own leftovers).
 */
export async function removeSyncedTemplate(
  io: StoreIO,
  templateId: string,
  /** Engine binding, so the template's local CASE IMAGE is dropped too (native encrypted
   *  store on desktop, IndexedDB on web). Optional so existing callers keep compiling; when
   *  omitted the image is left behind exactly as before. */
  engine?: Parameters<typeof deleteTemplateImage>[0],
): Promise<void> {
  const t = await readSyncedTemplate(io, templateId);
  await io.localStore.records.remove(SYNCED_TEMPLATES, templateId);
  const versionId = t?.currentVersion?.versionId;
  if (!versionId) return;
  // Every view the template BAKED: its authored views plus the primary one, which owns the flat
  // (legacy, view-less) refs and is therefore always a possible key even when `views` is empty.
  const views = new Set<string>([PRIMARY_VIEW]);
  for (const v of t?.currentVersion?.payload?.views ?? []) if (v.view_type) views.add(v.view_type);
  await Promise.all([...views].map((v) => deleteMockup(versionId, v)));
  // …and the template's local CASE IMAGE, which outlived the record the same way.
  if (engine) await deleteTemplateImage(engine, templateId);
}

/** Every template synced to this device, with the write stamp the eviction rules read. */
export async function listSyncedTemplateRows(io: StoreIO): Promise<SyncedTemplateRow[]> {
  const rows = await io.localStore.records.all<unknown>(SYNCED_TEMPLATES);
  const out: SyncedTemplateRow[] = [];
  for (const r of rows) {
    const row = await unwrapRow(r);
    if (row) out.push(row);
  }
  return out;
}

/** Every template synced to this device (full records, with geometry), decrypted. */
export async function listSyncedTemplates(io: StoreIO): Promise<ProductTemplate[]> {
  return (await listSyncedTemplateRows(io)).map((r) => r.template);
}

/** The ids synced to this device — read WITHOUT decrypting (the id is the clear row key). */
export async function listSyncedTemplateIds(io: StoreIO): Promise<string[]> {
  const rows = await io.localStore.records.all<unknown>(SYNCED_TEMPLATES);
  return rows.map((r) =>
    isEncRow(r) || isPlainRow(r) ? r.id : (r as ProductTemplate).templateId);
}

/** Cache one catalog list locally (encrypted), so an OFFLINE surface can resolve names
 *  without a network call. Shared by the phone-model and case-style pairs below. */
async function saveCatalogList<T>(io: StoreIO, cacheKey: string, rows: T[]): Promise<void> {
  const key = await getBlobKey();
  const value = key ? { enc: await encryptString(key, JSON.stringify(rows)) } : rows;
  await io.localStore.kv.set(cacheKey, value);
}

/** Read one cached catalog list (empty when nothing has been cached, or it cannot be read). */
async function listCatalogList<T>(io: StoreIO, cacheKey: string): Promise<T[]> {
  const raw = await io.localStore.kv.get<unknown>(cacheKey);
  if (!raw) return [];
  if (isEncRow(raw) || (raw && typeof raw === "object" && typeof (raw as { enc?: string }).enc === "string")) {
    const key = await getBlobKey();
    if (!key) return [];
    try {
      return JSON.parse(await decryptString(key, (raw as { enc: string }).enc)) as T[];
    } catch {
      return [];
    }
  }
  return raw as T[]; // legacy plaintext array
}

/** Cache the phone-model catalog locally (encrypted) so the Mockup Generator can resolve
 *  brand/model labels without a network call. Best-effort enrichment — the tree falls back
 *  to each template's `display.model` when this is empty. */
export async function saveSyncedPhoneModels(io: StoreIO, models: PhoneModelOption[]): Promise<void> {
  await saveCatalogList(io, PHONE_MODELS_KEY, models);
}

/** The locally-cached phone-model catalog (empty when nothing has been synced). */
export async function listSyncedPhoneModels(io: StoreIO): Promise<PhoneModelOption[]> {
  return listCatalogList<PhoneModelOption>(io, PHONE_MODELS_KEY);
}

/**
 * Cache the case-style catalog locally, the twin of the phone-model cache.
 *
 * F-7 / review-2 item 1: the Mock-up funnel is OFFLINE-first by contract (it reads the
 * templates synced to this device, never the network), but it still has to name the case
 * style in the marketplace sheet and in the SKU. Without this it fell back to the template's
 * own name, so a funnel run shipped `ONEPLUS-6-TEMPLATE-CASE-MAGENTA-ART`. Written by the
 * Templates page, which is where syncing happens and where the list is already fetched.
 */
export async function saveSyncedCaseStyles(io: StoreIO, styles: CaseStyleOption[]): Promise<void> {
  await saveCatalogList(io, CASE_STYLES_KEY, styles);
}

/** The locally-cached case-style catalog (empty when nothing has been synced). */
export async function listSyncedCaseStyles(io: StoreIO): Promise<CaseStyleOption[]> {
  return listCatalogList<CaseStyleOption>(io, CASE_STYLES_KEY);
}
